/**
 * <SelfReportedSourcePrompt> — "¿Cómo nos conociste?", asked once per person
 * and ONLY on a post-conversion success screen (lead submitted, checkout paid,
 * listing published). Mounting it before a submit button would turn a free
 * measurement into conversion friction — that is exactly why the 2026-07-15
 * pre-submit version was deferred (docs/seo/05-ai-search-visibility.md).
 *
 * One tap records the answer — no submit button, nothing required, leaving
 * the page is a valid "skip". Sources that invite a "¿quién?" get an optional
 * follow-up input; the chip is already saved by then.
 *
 * Copy lives in the `self_reported_source` namespace, LAZY-loaded: the prompt
 * only ever renders after a conversion, so SSR-ing it would ship its JSON in
 * `__NEXT_DATA__` to every visitor of every listing page for nothing.
 *
 * Never a conversion: PostHog events + a person property only. Do not add a
 * pixel, a TIER_ROLLUP_MAP entry, `generateLead` or `useSupplyLead` here.
 */

import CheckRounded from '@mui/icons-material/CheckRounded'
import { useTranslation } from 'next-i18next'
import { useId, useState } from 'react'

import BaseTextField from 'src/components/Inputs/BaseTextField'
import useLazyNamespace from 'src/hooks/useLazyNamespace'
import {
  SelfReportedSource,
  type SelfReportedSourceSurface
} from 'src/types/graphql'

import {
  SELF_REPORTED_DETAIL_MAX,
  SELF_REPORTED_SOURCE_OPTIONS,
  type SelfReportedAudience
} from 'src/components/SelfReportedSource/options'
import { useSelfReportedSource } from 'src/components/SelfReportedSource/useSelfReportedSource'

const NAMESPACE = 'self_reported_source'

type SelfReportedSourcePromptProps = {
  surface: SelfReportedSourceSurface
  audience: SelfReportedAudience
  /** Anonymous lead forms only — the contact the visitor just submitted. */
  contact?: { email?: string | null; phone?: string | null }
  className?: string
}

export default function SelfReportedSourcePrompt({
  surface,
  audience,
  contact,
  className
}: SelfReportedSourcePromptProps) {
  const { t } = useTranslation(NAMESPACE)
  const questionId = useId()
  const [detail, setDetail] = useState('')
  const { status, selected, choose, submitDetail } = useSelfReportedSource({
    surface,
    audience,
    contact
  })
  // Fetch the copy only once we know we will actually ask.
  const isVisible = status !== 'pending' && status !== 'hidden'
  const copyReady = useLazyNamespace(NAMESPACE, isVisible)

  if (!isVisible || !copyReady) return null

  // Literal keys only — typed t() rejects template-literal keys.
  const optionLabels: Record<SelfReportedSource, string> = {
    [SelfReportedSource.GoogleSearch]: t('OPTIONS.GOOGLE_SEARCH'),
    [SelfReportedSource.GoogleMaps]: t('OPTIONS.GOOGLE_MAPS'),
    [SelfReportedSource.AiAssistant]: t('OPTIONS.AI_ASSISTANT'),
    [SelfReportedSource.SocialMedia]: t('OPTIONS.SOCIAL_MEDIA'),
    [SelfReportedSource.Referral]: t('OPTIONS.REFERRAL'),
    [SelfReportedSource.OtherWebsite]: t('OPTIONS.OTHER_WEBSITE'),
    [SelfReportedSource.SpotmeOutreach]: t('OPTIONS.SPOTME_OUTREACH'),
    [SelfReportedSource.Other]: t('OPTIONS.OTHER')
  }
  const detailLabel =
    selected === SelfReportedSource.Referral
      ? t('DETAIL_LABEL.REFERRAL')
      : selected === SelfReportedSource.OtherWebsite
        ? t('DETAIL_LABEL.OTHER_WEBSITE')
        : t('DETAIL_LABEL.OTHER')

  const isAnswered = status === 'detail' || status === 'done'

  return (
    <section
      aria-labelledby={questionId}
      className={`border-t border-ink-100/15 pt-5 ${className ?? ''}`}
    >
      <p className="font-display text-[10px] tracking-[0.16em] text-ink-200 uppercase md:text-[11px]">
        {t('EYEBROW')}
      </p>
      <p
        id={questionId}
        className="mt-1 font-display text-[20px] leading-tight font-medium text-ink-700 italic md:text-[22px]"
      >
        {t('QUESTION')}
      </p>

      {status === 'asking' && (
        <>
          <p className="mt-1 text-[12px] text-ink-200 md:text-[13px]">
            {t('HINT')}
          </p>
          <div
            role="group"
            aria-labelledby={questionId}
            className="mt-4 flex flex-wrap gap-2"
          >
            {SELF_REPORTED_SOURCE_OPTIONS[audience].map((source) => (
              <button
                key={source}
                type="button"
                onClick={() => choose(source)}
                className="inline-flex min-h-11 items-center border border-ink-100/25 bg-white-500 px-3.5 py-2 text-[13px] text-ink-700 transition-colors hover:border-blue-500 hover:text-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:border-blue-500 active:bg-blue-500 active:text-white-500 motion-reduce:transition-none md:text-[14px]"
              >
                {optionLabels[source]}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Polite live region — mounted from the start (not display:none) so
          screen readers announce the thanks when it fills. */}
      <p
        aria-live="polite"
        className={
          isAnswered
            ? 'mt-3 flex items-center gap-2 text-[13px] text-ink-700 md:text-[14px]'
            : undefined
        }
      >
        {isAnswered && (
          <>
            <CheckRounded aria-hidden className="text-[16px]! text-blue-500" />
            {t('THANKS')}
          </>
        )}
      </p>

      {status === 'detail' && (
        <form
          className="mt-3 flex flex-col gap-2 md:flex-row md:items-end"
          onSubmit={(event) => {
            event.preventDefault()
            submitDetail(detail)
          }}
        >
          <div className="min-w-0 flex-1">
            <BaseTextField
              topLabel={detailLabel}
              fullWidth
              value={detail}
              onChange={(event) =>
                setDetail((event.target as HTMLInputElement).value)
              }
              slotProps={{
                htmlInput: {
                  // BaseTextField's topLabel is a <p>, not a <label for> —
                  // without this the input has no accessible name.
                  'aria-label': detailLabel,
                  maxLength: SELF_REPORTED_DETAIL_MAX,
                  autoComplete: 'off',
                  // 16px on mobile — anything smaller makes iOS Safari zoom.
                  className: 'text-base md:text-sm'
                }
              }}
            />
          </div>
          <button
            type="submit"
            className="min-h-11 shrink-0 border border-ink-700 bg-ink-700 px-5 text-[12px] font-medium tracking-[0.14em] text-white-500 uppercase transition-colors hover:border-blue-500 hover:bg-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 motion-reduce:transition-none md:h-10 md:min-h-0"
          >
            {t('DETAIL_SUBMIT')}
          </button>
        </form>
      )}
    </section>
  )
}
